import $ from 'jquery';
import _forEach from 'lodash/forEach';
import _isNaN from 'lodash/isNaN';
import AttackController from '../attack/AttackController';
import EventBus from '../lib/EventBus';
import { SELECTORS } from '../constants/selectors';
import { ATTACK_EVENT, EVENT } from '../constants/eventNames';
import { ATTACK_SETTING_UNITS } from '../constants/gameAttackConstants';

/**
 * @property UI_ATTACK_SETTINGS_MODAL_TEMPLATE
 * @type {string}
 * @final
 */
const UI_ATTACK_SETTINGS_MODAL_TEMPLATE = `
    <div class="option-dialog dialog">
        <p class="dialog-title">ADS-B Attacks</p>
        <div class="dialog-body nice-scrollbar"></div>
        <div class="dialog-footer form-element">
            <button id="attack-settings-reset-button" class="button">Reset attack settings and restart traffic</button>
            <button id="attack-traffic-restart-button" class="button">Restart the traffic</button>
        </div>
        <p class="dialog-text">For more information about attacks please hover the sword and press information.
        </p>
    </div>`;

/**
 * @property UI_OPTION_CONTAINER_TEMPLATE
 * @type {string}
 * @final
 */
const UI_OPTION_CONTAINER_TEMPLATE = '<div class="form-element"></div>';

/**
 * @property UI_OPTION_LABEL_TEMPLATE
 * @type {string}
 * @final
 */
const UI_OPTION_LABEL_TEMPLATE = '<span class="form-label"></span>';

/**
 * @property UI_OPTION_SELECTOR_TEMPLATE
 * @type {string}
 * @final
 */
const UI_OPTION_SELECTOR_TEMPLATE = '<span class="form-type-select"></span>';

/**
 * @property UI_OPTION_RANGE_TEMPLATE
 * @type {string}
 * @final
 */
const UI_OPTION_RANGE_TEMPLATE = '<span class="form-type-range"></span>';

/**
 * @property UI_OPTION_CATEGORY_TEMPLATE
 * @type {string}
 * @final
 */
const UI_OPTION_CATEGORY_TEMPLATE = `<div class="attack-option-category">
                                        <span class="attack-option-category-header"></span>
                                        <hr>
                                    </div>`;

/**
 * @class AttackSettingsController
 */
export default class AttackSettingsController {
    constructor($element) {
        /**
         * EventBus used to trigger events
         * 
         * @property _eventBus
         * @type {EventBus}
         * @default EventBus
         */
        this._eventBus = EventBus;

        /**
         * Root DOM element
         *
         * @property $element
         * @type {jquery|HTML Element}
         * @default $element
         */
        this.$element = $element;

        /**
         * Dialog DOM element
         *
         * @property $dialog
         * @type {jquery|HTML Element}
         * @default null
         */
        this.$dialog = null;

        /**
         * Dialog's body DOM element
         *
         * @property $dialogBody
         * @type {jquery|HTML Element}
         * @default null
         */
        this.$dialogBody = null;

        this._setup_handlers().enable().init();
    }

    /**
     * 
     * @for AttackSettingsController
     * @returns {AttackSettingsController}
     * @chainable
     */
    _setup_handlers() {
        this._resetAttackSettingsHandler = this._resetAttackSettings.bind(this);
        this._restartTrafficHandler = this._restartTraffic.bind(this);
        this._redrawAttackOptionsHandler = this.redrawAttackOptions.bind(this);

        return this;
    }

    /**
     * Enable event handlers
     * 
     * @returns {AttackSettingsController}
     * @chainable
     */
    enable() {
        this._eventBus.on(ATTACK_EVENT.ATTACK_SETTINGS_UPDATED, this._redrawAttackOptionsHandler);

        return this;
    }

    /**
     * Disable event handlers
     * 
     * @returns {AttackSettingsController}
     * @chainable
     */
    disable() {
        this._eventBus.off(ATTACK_EVENT.ATTACK_SETTINGS_UPDATED, this._redrawAttackOptionsHandler);

        return this;
    }


    /**
     *
     * @for AttackSettingsController
     * @method init
     * @chainable
     */
    init() {
        this.$dialog = $(UI_ATTACK_SETTINGS_MODAL_TEMPLATE);
        this.$dialogBody = this.$dialog.find(SELECTORS.DOM_SELECTORS.DIALOG_BODY);

        this._buildDialogBody();

        this.$element.append(this.$dialog);

        this.$dialog.find(SELECTORS.DOM_SELECTORS.ATTACK_SETTINGS_RESET_BUTTON).click(this._resetAttackSettingsHandler);
        this.$dialog.find(SELECTORS.DOM_SELECTORS.ATTACK_TRAFFIC_RESTART_BUTTON).click(this._restartTrafficHandler);

        return this;
    }

    _restartTraffic() {
        this._eventBus.trigger(EVENT.TRAFFIC_RESET);
    }

    _resetAttackSettings() {
        AttackController.attackOptions.reset();
        this._restartTraffic();
        this._buildDialogBody();
    }

    _buildDialogBody() {
        this.$dialogBody.empty();
        const descriptions = AttackController.attackOptions.getDescriptions();

        let optionsByCategory = {};

        _forEach(descriptions, (opt) => {
            if (!optionsByCategory[opt.category]) {
                optionsByCategory[opt.category] = [];
            }

            optionsByCategory[opt.category].push(opt);
        });

        _forEach(optionsByCategory, (options, category) => {
            const $category = $(UI_OPTION_CATEGORY_TEMPLATE);
            const $header = $category.find(SELECTORS.DOM_SELECTORS.ATTACK_OPTION_CATEGORY_HEADER);
            $header.text(category);

            _forEach(options, (opt) => {
                let $container;
                if (opt.type === 'select') {
                    $container = this._buildOptionTemplate(opt);
                } else if (opt.type === 'range') {
                    $container = this._buildRangeInputTemplate(opt);
                }

                if ($container) {
                    $category.append($container);
                }
            });


            this.$dialogBody.append($category);
        });

    }

    /**
     * Redraw the attack options
     * 
     * @for AttackSettingsController
     * @method redrawAttackOptions
     */
    redrawAttackOptions() {
        this._buildDialogBody();
    }

    /**
     * Returns whether the airport selection dialog is open
     *
     * @for AttackSettingsController
     * @method isDialogOpen
     * @return {boolean}
     */
    isDialogOpen() {
        return this.$dialog.hasClass(SELECTORS.CLASSNAMES.OPEN);
    }

    /**
    * @for AttackSettingsController
    * @method toggleDialog
    */
    toggleDialog() {
        this.$dialog.toggleClass(SELECTORS.CLASSNAMES.OPEN);
    }

    /**
     * Build the html for a game option and its corresponding value elements.
     *
     * @for AttackSettingsController
     * @method _buildOptionTemplate
     * @param option {object}
     * @return $container {jquery Element}
     * @private
     */
    _buildOptionTemplate(option) {
        const $container = $(UI_OPTION_CONTAINER_TEMPLATE);
        const $label = $(UI_OPTION_LABEL_TEMPLATE);
        const $optionSelector = $(UI_OPTION_SELECTOR_TEMPLATE);
        const $selector = $(`<select name="${option.name}"></select>`);
        const selectedOption = AttackController.attackOptions.getAttackOptionByName(option.name);

        $container.append($label);
        $label.text(option.description);

        // this could me done with a _map(), but verbosity here makes the code easier to read
        for (let i = 0; i < option.optionList.length; i++) {
            const $optionSelectTempalate = this._buildOptionSelectTemplate(option.optionList[i], selectedOption);

            $selector.append($optionSelectTempalate);
        }

        // TODO: this should be moved to proper event handler method and only assigned here.
        $selector.change((event) => {
            const $currentTarget = $(event.currentTarget);

            AttackController.attackOptions.setAttackOptionByName($currentTarget.attr('name'), $currentTarget.val());
        });

        $optionSelector.append($selector);
        $container.append($optionSelector);

        return $container;
    }

    /**
     * Build the html for a select option.
     *
     * @for AttackSettingsController
     * @method _buildOptionTemplate
     * @param optionData {array<string>}
     * @param selectedOption {string}
     * @return optionSelectTempalate {HTML Element}
     * @private
     */
    _buildOptionSelectTemplate(optionData, selectedOption) {
        // the `selectedOption` coming in to this method will always be a string (due to existing api) but
        // could contain valid numbers. here we test for valid number and build `parsedSelectedOption` accordingly.
        const parsedSelectedOption = !_isNaN(parseFloat(selectedOption)) ?
            parseFloat(selectedOption) :
            selectedOption;

        if (optionData.value === parsedSelectedOption) {
            return `<option value="${optionData.value}" selected>${optionData.displayLabel}</option>`;
        }

        return `<option value="${optionData.value}">${optionData.displayLabel}</option>`;
    }

    _buildRangeInputTemplate(option) {
        if (option.type !== 'range') {
            throw new TypeError(`Expected option.type to be "range", but received ${option.type}`);
        }
        const selectedOption = AttackController.attackOptions.getAttackOptionByName(option.name);

        const unit = option.unit;

        const $container = $(UI_OPTION_CONTAINER_TEMPLATE);
        const $label = $(UI_OPTION_LABEL_TEMPLATE);
        const $sliderContainer = $(UI_OPTION_RANGE_TEMPLATE);
        const $selector = $(`<input type="range" min="${option.minValue}" max="${option.maxValue}" step="0.01"></input>`);
        const $value = $(`<span>${Math.round(selectedOption * 100)}%</span>`);

        let step;
        let valueText;
        
        switch (unit) {
            case ATTACK_SETTING_UNITS.NM:
            case ATTACK_SETTING_UNITS.SECONDS:
                step = 1;
                valueText = `${selectedOption}${unit}`;
                break;
            case ATTACK_SETTING_UNITS.PERCENTAGE:
                step = 0.01;
                valueText = `${Math.round(selectedOption * 100)}${unit}`;
                break;
            default:
                step = 1;
                valueText = selectedOption;
        }
        
        $selector.attr('step', step);
        $selector.attr('unit', unit);
        $value.text(valueText);

        
        $container.append($label);
        $label.text(option.description);

        $sliderContainer.append($selector);
        $sliderContainer.append($value);

        $container.append($sliderContainer);

        $selector.val(selectedOption);

        $selector.on('input', (event) => {
            const $currentTarget = $(event.currentTarget);

            const unit = $currentTarget.attr('unit');

            const value = $currentTarget.val();

            switch (unit) {
                case ATTACK_SETTING_UNITS.NM:
                case ATTACK_SETTING_UNITS.SECONDS:
                case ATTACK_SETTING_UNITS.PERCENTAGE:
                    $value.text(this._formatValue(value, unit));
                    break;
                default:
                    $value.text(value);
                    break;
            }

        });

        $selector.on('change', (event) => {
            const $currentTarget = $(event.currentTarget);

            const unit = $currentTarget.attr('unit');

            const value = AttackController.attackOptions.setAttackOptionByName(option.name, $currentTarget.val());

            $currentTarget.val(value);

            switch (unit) {
                case ATTACK_SETTING_UNITS.NM:
                case ATTACK_SETTING_UNITS.SECONDS:
                case ATTACK_SETTING_UNITS.PERCENTAGE:
                    $value.text(this._formatValue(value, unit));
                    break;
                default:
                    $value.text(value);
                    break;
            }
        });


        return $container;
    }

    _formatValue(value, unit) {
        switch (unit) {
            case ATTACK_SETTING_UNITS.NM:
            case ATTACK_SETTING_UNITS.SECONDS:
                return `${value}${unit}`;
            case ATTACK_SETTING_UNITS.PERCENTAGE:
                return `${Math.round(value * 100)}${unit}`;
            default:
                return value;
        }
    }

    _buildAttackOnOffSwitch() {
        const $container = $(UI_OPTION_CONTAINER_TEMPLATE);
        const $label = $(UI_OPTION_LABEL_TEMPLATE);
        const $optionSelector = $(UI_OPTION_SELECTOR_TEMPLATE);
        const $selector = $(`<select name="attackOnOff"></select>`);
        const selectedOption = AttackController.attackOptions.getAttackOptionByName('attackOnOff');

        $container.append($label);
        $label.text('Attack On/Off');

        // this could me done with a _map(), but verbosity here makes the code easier to read
        for (let i = 0; i < option.optionList.length; i++) {
            const $optionSelectTempalate = this._buildOptionSelectTemplate(option.optionList[i], selectedOption);

            $selector.append($optionSelectTempalate);
        }
    }

}
